import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { InfoTooltip } from '~/components/InfoTooltip';
import { TerritoryLabel } from '~/components/TerritoryLabel/TerritoryLabel';
import { SortableIndicator } from '~/modules/artist-roster/components/Table/SortableIndicator';
import { formatPercent } from '~/modules/artist-roster/components/TrackDetails/components/StreamsTerritoryTable/utils';

import type { CampaignBenchmark } from '../lib/types';
import { formatCurrency } from '../lib/utils';

export function useBenchmarkColumns() {
  const { t } = useTranslation('campaign-planner', { keyPrefix: 'benchmarks' });
  const { t: nameTranslation } = useTranslation('names');
  const { t: territoryTranslation } = useTranslation('territories');

  const columnHelper = useMemo(
    () => createColumnHelper<CampaignBenchmark>(),
    [],
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor(
        (row) => territoryTranslation(`names.${row.market}`),
        {
          id: 'market',
          meta: {
            className: 'w-[20%]',
          },
          header: (header) => {
            return (
              <div className="flex items-center gap-xs text-subtle">
                <span>{t('header.market')}</span>
                <SortableIndicator column={header.column} />
              </div>
            );
          },
          cell: ({ row }) => (
            <span className="text-strong">
              <TerritoryLabel code={row.original.market} />
            </span>
          ),
        },
      ),
      columnHelper.accessor('objective', {
        id: 'objective',
        header: (header) => (
          <div className="flex items-center gap-xs text-subtle">
            <span>{t('header.objective')}</span>
            <SortableIndicator column={header.column} />
          </div>
        ),
        cell: ({ row }) => (
          <span className="text-strong">{row.original.objective}</span>
        ),
      }),
      columnHelper.accessor('platform', {
        id: 'platform',
        header: (header) => (
          <div className="flex items-center gap-xs text-subtle">
            <span>{t('header.platform')}</span>
            <SortableIndicator column={header.column} />
          </div>
        ),
        cell: ({ row }) =>
          row.original.platform ? (
            <div className="flex items-center gap-sm">
              <Icon
                type={row.original.platform.toLowerCase()}
                variant="brands"
                size="md"
                aria-label={t('header.platform')}
              />
              <span className="text-strong">
                {nameTranslation(row.original.platform)}
              </span>
            </div>
          ) : (
            <span className="text-strong">--</span>
          ),
      }),
      // Disable platformFollowers column for now as data is not available
      // columnHelper.accessor('platformFollowers', {
      //   id: 'platformFollowers',
      //   header: () => (
      //     <span className="flex justify-center text-subtle">
      //       {t('header.platformFollowers')}
      //     </span>
      //   ),
      //   cell: ({ row }) => (
      //     <span className="flex justify-center font-semibold">
      //       {formatCount(row.original.platformFollowers)}
      //     </span>
      //   ),
      // }),
      columnHelper.accessor('CPM', {
        id: 'CPM',
        meta: {
          className: 'w-[8%]',
        },
        header: (header) => (
          <span className="flex items-center gap-xs text-subtle">
            {t('header.CPM')}
            <InfoTooltip content={t('tooltip.CPM')} />
            <SortableIndicator column={header.column} />
          </span>
        ),
        cell: ({ row }) => (
          <span className="font-semibold">
            {formatCurrency({
              value: row.original.CPM,
              minimumDecimals: 3,
              maximumDecimals: 3,
            })}
          </span>
        ),
      }),
      columnHelper.accessor('CPV', {
        id: 'CPV',
        meta: {
          className: 'w-[8%]',
        },
        header: (header) => (
          <span className="flex items-center gap-xs text-subtle">
            {t('header.CPV')}
            <InfoTooltip content={t('tooltip.CPV')} />
            <SortableIndicator column={header.column} />
          </span>
        ),
        cell: ({ row }) => (
          <span className="font-semibold">
            {formatCurrency({
              value: row.original.CPV,
              minimumDecimals: 3,
              maximumDecimals: 3,
            })}
          </span>
        ),
      }),

      columnHelper.accessor('CPC', {
        id: 'CPC',
        meta: {
          className: 'w-[8%]',
        },
        header: (header) => (
          <span className="flex items-center gap-xs text-subtle">
            {t('header.CPC')}
            <InfoTooltip content={t('tooltip.CPC')} />
            <SortableIndicator column={header.column} />
          </span>
        ),
        cell: ({ row }) => (
          <span className="font-semibold">
            {formatCurrency({
              value: row.original.CPC,
              minimumDecimals: 3,
              maximumDecimals: 3,
            })}
          </span>
        ),
      }),
      columnHelper.accessor('VTR', {
        id: 'VTR',
        meta: {
          className: 'w-[8%]',
        },
        header: (header) => (
          <span className="flex items-center gap-xs text-subtle">
            {t('header.VTR')}
            <InfoTooltip content={t('tooltip.VTR')} />
            <SortableIndicator column={header.column} />
          </span>
        ),
        cell: ({ row }) =>
          row.original.VTR ? (
            <span className="font-semibold">
              {formatPercent(row.original.VTR * 100, 3)}
            </span>
          ) : (
            <span className="pl-lg font-semibold">--</span>
          ),
      }),
      columnHelper.accessor('CTR', {
        id: 'CTR',
        meta: {
          className: 'w-[8%]',
        },
        header: (header) => (
          <span className="flex items-center gap-xs text-subtle">
            {t('header.CTR')}
            <InfoTooltip content={t('tooltip.CTR')} />
            <SortableIndicator column={header.column} />
          </span>
        ),
        cell: ({ row }) =>
          row.original.CTR ? (
            <span className="font-semibold">
              {formatPercent(row.original.CTR * 100, 3)}
            </span>
          ) : (
            <span className="pl-lg font-semibold">--</span>
          ),
      }),
      columnHelper.accessor('engagementRate', {
        id: 'engagementRate',
        meta: {
          className: 'w-[8%]',
        },
        header: (header) => (
          <span className="flex items-center justify-center gap-xs text-subtle">
            {t('header.engagementRate')}
            <InfoTooltip content={t('tooltip.engagementRate')} />
            <SortableIndicator column={header.column} />
          </span>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            {row.original.engagementRate ? (
              <span className="font-semibold">
                {formatPercent(row.original.engagementRate * 100, 3)}
              </span>
            ) : (
              <span className="font-semibold">--</span>
            )}
          </div>
        ),
      }),
    ];
  }, [columnHelper, t, nameTranslation, territoryTranslation]);

  return { columns };
}
